/**
 * Order-book fulfilment arithmetic: what was ordered, what the pharmacy handed
 * over, what the ward accepted — and the user-facing labels derived from them.
 *
 * Import-free beyond ./prescriptionStatus (itself constants-only) on purpose:
 * pharmacyMappers reaches axios ESM, which Jest cannot load. Same escape hatch
 * as prescriptionStatus / dispensingEntity.
 *
 * Quantity semantics (pharamcy.md §1, verified against order_lifecycle.py):
 *   - ordered   = Σ details.total_quantity — NULL/0 means UNKNOWN, never zero.
 *                 9 of 14 rows on admission 66 have no denominator; an unknown
 *                 total must render as unknown, never as a guessed number.
 *   - delivered = Σ delivered_quantity — the pharmacy's own figure; drives
 *                 "what is left to fill" (accepted lags until the ward confirms).
 *   - accepted  = Σ accepted_quantity — the ward's figure; the only thing that
 *                 drives the status band, so progress labels ("12/30") use it.
 *   - delivered > accepted is the durable "delivered, ward hasn't confirmed" signal.
 */
import {
  isActivePrescriptionStatus,
  normalizePrescriptionStatus,
} from './prescriptionStatus';

/** Non-terminal rows older than this are the historical backlog (pharamcy.md §4.4). */
export const STALE_ORDER_AGE_DAYS = 7;

const DAY_MS = 24 * 60 * 60 * 1000;

function toNum(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

/** Counters are numeric(12,3); show "12", "12.5" — never "12.000". */
export function formatQty(value) {
  return String(parseFloat(toNum(value).toFixed(3)));
}

/**
 * A line's ordered quantity, or null when it is not knowable. total_quantity is
 * omitted/0 for regimens with no computable total — the backend reads that as
 * "unknown, never a guess" (order_lifecycle.ordered_quantity) and so do we.
 */
function lineOrderedQuantity(line) {
  const details = line && typeof line.details === 'object' && line.details !== null
    ? line.details
    : {};
  const total = Number(details.total_quantity ?? line?.total_quantity);
  return Number.isFinite(total) && total > 0 ? total : null;
}

/**
 * Roll one order's medicines[] up into the numbers every fulfilment surface
 * needs. `ordered`/`remaining` are null (not 0) when ANY line lacks a
 * denominator — a partial sum would understate the order and make "12 of 20"
 * read as nearly done on a 30-unit order.
 */
export function summarizeFulfilment(medicines) {
  const lines = Array.isArray(medicines) ? medicines : [];
  let ordered = 0;
  let delivered = 0;
  let accepted = 0;
  let hasUnknownOrdered = lines.length === 0;

  lines.forEach((line) => {
    const lineOrdered = lineOrderedQuantity(line);
    if (lineOrdered == null) hasUnknownOrdered = true;
    else ordered += lineOrdered;
    delivered += toNum(line?.delivered_quantity);
    accepted += toNum(line?.accepted_quantity);
  });

  return {
    ordered: hasUnknownOrdered ? null : ordered,
    delivered,
    accepted,
    remaining: hasUnknownOrdered ? null : Math.max(0, ordered - delivered),
    hasUnknownOrdered,
  };
}

/**
 * Status-chip label override for the quantity-bearing statuses, or null to use
 * the default label. Rules from pharamcy.md §4.1:
 *   Partial                      -> "Partially Dispensed 12/30"
 *   ShortClosed                  -> "Closed (Short) 12/30"
 *   ShortClosed, nothing accepted -> "Not Supplied"   (display rule, not a status)
 * The X/Y pair is accepted/ordered — accepted is what drives the band, so the
 * chip must agree with the status it decorates. Unknown denominator drops the
 * suffix rather than inventing one.
 */
export function prescriptionStatusChipLabel(status, fulfilment) {
  const normalized = normalizePrescriptionStatus(status);
  const accepted = toNum(fulfilment?.accepted);
  const ordered = fulfilment?.ordered;
  const suffix = ordered != null ? ` ${formatQty(accepted)}/${formatQty(ordered)}` : '';

  if (normalized === 'SHORT_CLOSED') {
    if (accepted <= 0) return 'Not Supplied';
    return `Closed (Short)${suffix}`;
  }
  if (normalized === 'PARTIAL') {
    return `Partially Dispensed${suffix}`;
  }
  return null;
}

/**
 * Row-action label for a Partial row. Remaining is ordered − DELIVERED (the
 * pharmacy's own figure) — subtracting the ward's accepted figure would offer
 * units already handed over for dispense a second time.
 */
export function fillRemainingLabel(fulfilment) {
  if (!fulfilment || fulfilment.ordered == null) return 'Fill remaining';
  return `Fill remaining (${formatQty(fulfilment.remaining)} of ${formatQty(fulfilment.ordered)})`;
}

/** Stock has left the pharmacy that the ward has not yet confirmed taking. */
export function hasUnconfirmedDelivery(fulfilment) {
  if (!fulfilment) return false;
  return toNum(fulfilment.delivered) > toNum(fulfilment.accepted);
}

/**
 * Everything ordered has been handed over — nothing left for the pharmacy to
 * fill, only the ward's confirm outstanding. False when the denominator is
 * unknown: with no total we cannot claim "fully", and the start page can still
 * legitimately dispense more.
 */
export function isFullyDelivered(fulfilment) {
  if (!fulfilment || fulfilment.ordered == null) return false;
  return toNum(fulfilment.delivered) >= toNum(fulfilment.ordered);
}

/** Whole days since the order was created, or null when the date is unparseable. */
export function prescriptionAgeDays(createdAt, now = Date.now()) {
  const created = new Date(createdAt || '').getTime();
  if (!Number.isFinite(created)) return null;
  return Math.floor((now - created) / DAY_MS);
}

/**
 * The historical backlog: still-open rows nobody has touched in over a week.
 * NULL-counter detection is impossible here — the serializer coerces NULL to 0
 * (manage_prescription.py) — so age is the only client-side stale signal.
 */
export function isStalePrescription(row, now = Date.now()) {
  if (!isActivePrescriptionStatus(row?.status)) return false;
  const age = prescriptionAgeDays(row?.created_at, now);
  return age != null && age > STALE_ORDER_AGE_DAYS;
}
